import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Add, ArrowBack } from "@mui/icons-material";
import { useCreateSite, useSite, useUpdateSite } from "@/api/hooks/sites";
import { useOrganizations } from "@/api/hooks/organizations";
import { useContacts, useCreateContact } from "@/api/hooks/contacts";
import {
  DATA_ACCESS_MODELS,
  DATA_ACCESS_MODEL_LABELS,
  PIPELINE_STAGES,
  PIPELINE_STAGE_LABELS,
  TARGET_TIERS,
  TARGET_TIER_LABELS,
} from "@/api/labels";
import type {
  DataAccessModel,
  PipelineStage,
  SiteCreate,
  SiteUpdate,
  TargetTier,
} from "@/api/types";
import { US_STATES, findUsState, type UsState } from "@/utils/usStates";
import ContactForm, {
  EMPTY_CONTACT_DRAFT,
  contactDraftToPayload,
} from "./ContactForm";

interface FormState {
  display_id: string;
  common_name: string;
  legal_entity_name: string;
  target_tier: TargetTier;
  pipeline_stage: PipelineStage;
  data_access_model: DataAccessModel | "";
  emr: string;
  prior_relationship_notes: string;
  expected_contract_date: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  allowed_email_domains: string;
  primary_contact_id: string;
}

const EMPTY: FormState = {
  display_id: "",
  common_name: "",
  legal_entity_name: "",
  target_tier: "not_targeted",
  pipeline_stage: "qualified",
  data_access_model: "",
  emr: "",
  prior_relationship_notes: "",
  expected_contract_date: "",
  street: "",
  city: "",
  state: "",
  postal_code: "",
  allowed_email_domains: "",
  primary_contact_id: "",
};

export default function SiteForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const existing = useSite(id);
  const createMutation = useCreateSite();
  const updateMutation = useUpdateSite(id ?? "");
  const { data: organizations } = useOrganizations();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [addingContact, setAddingContact] = useState(false);
  const { data: contacts } = useContacts(isEdit ? id : undefined);
  const createContactMutation = useCreateContact(id ?? "");

  useEffect(() => {
    if (!isEdit || !existing.data) return;
    const s = existing.data;
    setOrganizationId(s.organization_id);
    setForm({
      display_id: s.display_id !== null ? String(s.display_id) : "",
      common_name: s.common_name,
      legal_entity_name: s.legal_entity_name ?? "",
      target_tier: s.target_tier,
      pipeline_stage: s.pipeline_stage,
      data_access_model: s.data_access_model ?? "",
      emr: s.emr ?? "",
      prior_relationship_notes: s.prior_relationship_notes ?? "",
      expected_contract_date: s.expected_contract_date ?? "",
      street: s.street ?? "",
      city: s.city ?? "",
      state: s.state ?? "",
      postal_code: s.postal_code ?? "",
      allowed_email_domains: (s.allowed_email_domains ?? []).join(", "),
      primary_contact_id: s.primary_contact_id ?? "",
    });
  }, [existing.data, isEdit]);

  const onChange = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const buildSharedFields = () => {
    const domains = form.allowed_email_domains
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean);
    return {
      organization_id: organizationId,
      common_name: form.common_name.trim(),
      legal_entity_name: form.legal_entity_name.trim() || null,
      target_tier: form.target_tier,
      pipeline_stage: form.pipeline_stage,
      data_access_model: form.data_access_model || null,
      emr: form.emr.trim() || null,
      prior_relationship_notes: form.prior_relationship_notes.trim() || null,
      expected_contract_date: form.expected_contract_date || null,
      street: form.street.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      postal_code: form.postal_code.trim() || null,
      allowed_email_domains: domains,
      primary_contact_id: form.primary_contact_id || null,
    };
  };

  const buildCreatePayload = (): SiteCreate => ({
    ...buildSharedFields(),
    // Parse to int so the API gets the right shape. Empty input
    // (leave it blank) becomes null and the API auto-allocates via
    // the Postgres sequence.
    display_id: form.display_id.trim()
      ? Number(form.display_id.trim())
      : null,
  });

  // display_id is immutable post-create and the API rejects it on PATCH
  // with extra_forbidden, so we leave it off the update payload entirely.
  const buildUpdatePayload = (): SiteUpdate => buildSharedFields();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!form.common_name.trim()) {
      setSubmitError("Site name is required.");
      return;
    }
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(buildUpdatePayload());
        navigate(`/clinops/sites/${id}`);
      } else {
        const created = await createMutation.mutateAsync(buildCreatePayload());
        navigate(`/clinops/sites/${created.id}`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save site");
    }
  };

  if (isEdit && existing.isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(isEdit ? `/clinops/sites/${id}` : "/clinops/sites")}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? "Edit site" : "New site"}</Typography>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Identity
            </Typography>
            <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
              <Autocomplete
                options={organizations ?? []}
                getOptionLabel={(o) => o.name}
                value={(organizations ?? []).find((o) => o.id === organizationId) ?? null}
                onChange={(_, value) => setOrganizationId(value?.id ?? null)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    helperText="Optional — leave blank for stand-alone Sites."
                  />
                )}
              />
              <TextField
                label="Site ID"
                value={form.display_id}
                onChange={(e) => onChange("display_id", e.target.value)}
                helperText="Optional — numeric only. Leave blank to auto-assign the next available number."
                slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*" } }}
                disabled={isEdit}
              />
              <TextField
                required
                label="Site name (common name)"
                value={form.common_name}
                onChange={(e) => onChange("common_name", e.target.value)}
              />
              <TextField
                label="Legal entity (contracted) name"
                value={form.legal_entity_name}
                onChange={(e) => onChange("legal_entity_name", e.target.value)}
              />
              <TextField
                multiline
                minRows={2}
                label="Prior relationship notes"
                value={form.prior_relationship_notes}
                onChange={(e) =>
                  onChange("prior_relationship_notes", e.target.value)
                }
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Corporate address & contact
            </Typography>
            <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
              <TextField
                label="Street"
                value={form.street}
                onChange={(e) => onChange("street", e.target.value)}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: 2,
                }}
              >
                <TextField
                  label="City"
                  value={form.city}
                  onChange={(e) => onChange("city", e.target.value)}
                />
                <Autocomplete<UsState>
                  options={US_STATES.slice()}
                  value={findUsState(form.state)}
                  onChange={(_, value) => onChange("state", value?.code ?? "")}
                  getOptionLabel={(s) => `${s.code} — ${s.name}`}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  renderInput={(params) => (
                    <TextField {...params} label="State" />
                  )}
                />
                <TextField
                  label="Postal code"
                  value={form.postal_code}
                  onChange={(e) => onChange("postal_code", e.target.value)}
                />
              </Box>
              <TextField
                label="Allowed email domains"
                value={form.allowed_email_domains}
                onChange={(e) =>
                  onChange("allowed_email_domains", e.target.value)
                }
                helperText="Comma-separated, e.g. memorial.org, mhs.edu"
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <TextField
                  select
                  label="Primary site contact"
                  value={form.primary_contact_id}
                  onChange={(e) =>
                    onChange("primary_contact_id", e.target.value)
                  }
                  disabled={!isEdit}
                  helperText={
                    isEdit
                      ? "Pick from this site's contacts, or add a new one."
                      : "Save the site first, then come back to add contacts and set this."
                  }
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="">— none —</MenuItem>
                  {(contacts ?? []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                      {c.title ? ` · ${c.title}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
                {isEdit && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setAddingContact(true)}
                    disabled={addingContact}
                    sx={{ mt: "8px", flexShrink: 0 }}
                  >
                    Add contact
                  </Button>
                )}
              </Box>
              {isEdit && addingContact && (
                <ContactForm
                  initial={EMPTY_CONTACT_DRAFT}
                  submitLabel="Add"
                  onCancel={() => setAddingContact(false)}
                  onSubmit={async (d) => {
                    const created =
                      await createContactMutation.mutateAsync(
                        contactDraftToPayload(d),
                      );
                    onChange("primary_contact_id", created.id);
                    setAddingContact(false);
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Lifecycle
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                select
                label="Pipeline stage"
                value={form.pipeline_stage}
                onChange={(e) =>
                  onChange("pipeline_stage", e.target.value as PipelineStage)
                }
              >
                {PIPELINE_STAGES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {PIPELINE_STAGE_LABELS[s]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Target tier / stratification"
                value={form.target_tier}
                onChange={(e) =>
                  onChange("target_tier", e.target.value as TargetTier)
                }
              >
                {TARGET_TIERS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {TARGET_TIER_LABELS[t]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Data access model"
                value={form.data_access_model}
                onChange={(e) =>
                  onChange(
                    "data_access_model",
                    e.target.value as DataAccessModel | "",
                  )
                }
              >
                <MenuItem value="">(not set)</MenuItem>
                {DATA_ACCESS_MODELS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {DATA_ACCESS_MODEL_LABELS[m]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="EMR"
                placeholder="e.g. Epic, Cerner"
                value={form.emr}
                onChange={(e) => onChange("emr", e.target.value)}
              />
              <TextField
                label="Expected contract date"
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.expected_contract_date}
                onChange={(e) =>
                  onChange("expected_contract_date", e.target.value)
                }
              />
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Link to={isEdit ? `/clinops/sites/${id}` : "/clinops/sites"}>
            <Button variant="text">Cancel</Button>
          </Link>
          <Button
            type="submit"
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? "Saving…"
              : isEdit
                ? "Save changes"
                : "Create site"}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
